import {action, comparer, computed, makeObservable, observable, reaction, runInAction} from "mobx";
import {project, projectStub, projectUuid, syncHandler, syncData} from "../model/index.js";
import app from "../app.js";
import {getDateString, loadObject} from "../utils/index.js";
import Uuid from "../../utils/uuid.js";
import {Sync, outSyncHandler} from "../sync/index.js";
import {Store} from "./index.js";
import {fetchLockOptions} from "./common.js";
import AsyncLock from "async-lock";

export class ProjectStore implements Store {
	_projects: Record<projectUuid, project> = {};
	_fetchLock = new AsyncLock(fetchLockOptions);

	constructor() {
		makeObservable(this, {
			all:           computed,
			create:        action,
			_projects:     observable,
			_clear:        action,
			_load:         action,
			_loadMultiple: action,
			_remove:       action
		});
	}

	_clear() {
		this._projects = {};
	}

	_refresh() {
		// We always refresh all the projects even if they were not loaded before (other stores work differently).
		// noinspection JSIgnoredPromiseFromCall
		this._fetchAll(true);
	}

	get all(): project[] {
		// noinspection JSIgnoredPromiseFromCall
		this._fetchAll();

		return Object.values(this._projects); // TODO: sort?
	}

	byUuid(uuid: projectUuid): project | undefined {
		// noinspection JSIgnoredPromiseFromCall
		this._fetch(uuid);

		return this._projects[uuid];
	}

	_isProjectLoaded(projectUuid: projectUuid): boolean {
		return this._projects[projectUuid] !== undefined;
	}

	async _fetch(uuid: projectUuid, force = false) {
		await this._fetchLock.acquire("_fetch:" + uuid, async () => {
			if (!app.isLoggedIn)
				return;
			if (force || !this._isProjectLoaded(uuid))
				this._load(await app.backend.getProject(uuid));
		});
	}

	async _fetchAll(force = false) {
		await this._fetchLock.acquire("_fetchAll", async () => {
			if (!app.isLoggedIn)
				return;
			if (force || Object.keys(this._projects).length === 0) {
				const projects = await app.backend.getProjects();

				runInAction(() => {
					if (force)
						this._clear();

					this._loadMultiple(projects);
				});
			}
		});
	}

	_load(project: project) {
		loadObject(this._projects, project.uuid, project);
	}

	_loadMultiple(projects: project[]) {
		projects.map(this._load.bind(this));
	}

	_remove(uuid: projectUuid) {
		delete this._projects[uuid];
	}

	static unstub(stub: projectStub): project {
		return Object.assign({
			uuid:         Uuid.base64() as projectUuid,
			account_uuid: app.accountUuid,
			description:  null,
			status:       "active",
			created_dt:   getDateString(),
			updated_dt:   null
		}, stub);
	}

	async create(data: projectStub): Promise<project> {
		const project = ProjectStore.unstub(data);

		// We can't load a project to memory immediately because it involves more objects that the API has to create,
		// like its initial topics. In this case, let's just wait for it to return, but we'll eventually need to deal
		// with this.

		await app.backend.createProject(project);
		this._load(project);
		return project;
	}

	_setupSyncHandlers(sync: Sync) {
		sync.setHandler(syncHandler.PROJECT_ADD, this._load.bind(this));
		sync.setHandler(syncHandler.PROJECT_MODIFY, this._load.bind(this));
		sync.setHandler(syncHandler.PROJECT_DELETE, ({project_uuid}: syncData[syncHandler.PROJECT_DELETE]) =>
			this._remove(project_uuid)
		);
	}

	_setupOutSyncHandlers(handler: outSyncHandler) {
		reaction(() => JSON.parse(JSON.stringify(this._projects)), (current, previous) => {
			if (!app.isLoggedIn)
				return;

			Object.keys(current)
				.filter(uuid => previous[uuid] === undefined)
				.map(uuid => handler(null, syncHandler.PROJECT_ADD, current[uuid]));

			Object.keys(previous)
				.filter(uuid => current[uuid] === undefined)
				.map(uuid => handler(null, syncHandler.PROJECT_DELETE, {project_uuid: uuid}));

			Object.keys(current)
				.filter(
					uuid => previous[uuid] !== undefined
						&& !comparer.structural(current[uuid], previous[uuid])
				)
				.map(uuid => handler(null, syncHandler.PROJECT_MODIFY, current[uuid]));
		});
	}
}

export default new ProjectStore();
